import { addMultiEvent, clamp } from '@soitron/global-helpers';

/**
 * @typedef {{value?: number, min?: number, max?: number, step?: number, sliderSelector?: string, sliderValueSelector?: string}} TSliderOptions
 * @description Configuration options for the SliderController.
 * @property {number} [value=1.5] The initial value of the slider.
 * @property {number} [min=0.5] The minimum allowed value for the slider.
 * @property {number} [max=2.5] The maximum allowed value for the slider.
 * @property {number} [step=0.025] The granular step size of the slider.
 * @property {string} [sliderSelector='#region-of-interest-slider'] CSS selector to find the <input type="range"> element within the wrapper.
 * @property {string} [sliderValueSelector='.scale-factor-value'] CSS selector to find the element that displays the current value.
 */

/**
 * @typedef {function(value?: number, options?: TSliderOptions): void} TChangeCallback
 * @description Callback function signature executed when the slider value changes (on 'input' event).
 * @param {number} [value] The new current value of the slider.
 * @param {TSliderOptions} [options] The frozen, current options of the controller.
 */

/**
 * @class
 * @classdesc A controller class to initialize, manage, and handle events for an HTML range input (slider) element.
 * It provides methods for setting min/max/step/value and handling UI state (show/hide).
 * @example
 * // HTML structure required:
 * // <div id="slider-wrapper">
 * //   <input type="range" id="my-range-slider">
 * //   <span class="value-display"></span>
 * // </div>
 * * const wrapper = document.getElementById('slider-wrapper');
 * const handler = new SliderController(wrapper, {
 * sliderSelector: '#my-range-slider',
 * sliderValueSelector: '.value-display',
 * max: 5,
 * step: 0.1
 * });
 * * handler.__onValueChange((value) => {
 * console.log('New slider value:', value);
 * });
 * * handler.__show(); // Make the slider active and visible
 */
export class SliderController {
  /**
   * @type {HTMLDivElement}
   * @readonly
   * @private
   * @description The main container element that holds the slider and value display.
   */
  _containerElement; // Pôvodne _wrapper

  /**
   * @type {HTMLInputElement}
   * @readonly
   * @private
   * @description The HTML <input type="range"> element.
   * */
  _rangeInput;

  /**
   * @type {HTMLSpanElement}
   * @readonly
   * @private
   * @description The element used to display the current numeric value of the slider.
   * */
  _valueDisplay;

  /**
   * @type {number}
   * @private
   * @description Index counter for storing AbortController instances.
   */
  _abortIndex = 0;

  /**
   * @type {AbortController[]}
   * @private
   * @description Array of AbortController instances used to cleanup event listeners on dispose.
   */
  _abortControllers = [];

  /**
   * @type {TChangeCallback}
   * @private
   * @description The registered callback function for slider value changes.
   */
  _changeListener;

  /**
   * @type {boolean}
   * @private
   * @description Flag indicating whether the slider control is currently active (visible and enabled).
   */
  _isActive = false;

  /**
   * @type {TSliderOptions}
   * @private
   * @description Current configuration options, including value, min, max, and selectors.
   */
  _config = {
    value: 1.5,
    min: 0.5,
    max: 2.5,
    step: 0.025,
    sliderSelector: '#slider',
    sliderValueSelector: '.scale-factor-value',
  };

  /**
   * @returns {boolean} The current active state of the slider (true if shown and enabled).
   * @public
   */
  get __isActive() {
    return this._isActive;
  }

  /**
   * @returns {number | undefined} The current numeric value of the slider.
   * @public
   */
  get __value() {
    return this._config.value;
  }

  /**
   * @param {number} value The new value to set. Will be clamped between min and max.
   * @public
   */
  set __value(value) {
    value = clamp(value, +this._config.min, +this._config.max);
    this._rangeInput.value = value.toString();
    this._config.value = value;
    this._changeListener?.(this._config.value, Object.freeze({ ...this._config }));
    this._updateValueDisplay(this._config.value);
  }

  /**
   * @returns {number | undefined} The minimum value of the slider.
   * @public
   */
  get __min() {
    return this._config.min;
  }

  /**
   * @param {number} value The new minimum value.
   * @public
   */
  set __min(value) {
    this._config.min = value;
    this._rangeInput.min = value.toString();

    // Re-clamp the current value to respect the new limits.
    // eslint-disable-next-line no-self-assign
    this.__value = this.__value;
  }

  /**
   * @returns {number | undefined} The maximum value of the slider.
   * @public
   */
  get __max() {
    return this._config.max;
  }

  /**
   * @param {number} value The new maximum value.
   * @public
   */
  set __max(value) {
    this._config.max = value;
    this._rangeInput.max = value.toString();

    // Re-clamp the current value to respect the new limits.
    // eslint-disable-next-line no-self-assign
    this.__value = this.__value;
  }

  /**
   * @returns {number | undefined} The step size of the slider.
   * @public
   */
  get __step() {
    return this._config.step;
  }

  /**
   * @param {number} value The new step size.
   * @public
   */
  set __step(value) {
    this._config.step = value;
    this._rangeInput.step = value.toString();
  }

  /**
   * @constructor
   * @param {HTMLElement} wrapper The wrapper element containing the slider and value display.
   * @param {TSliderOptions} [options] Optional configuration options to override defaults.
   * @public
   * @throws {Error} If the wrapper element is not provided.
   * @throws {Error} If the slider input element is not found using `sliderSelector`.
   */
  constructor(wrapper, options) {
    this._containerElement = wrapper;
    if (!this._containerElement) {
      throw new Error('SliderController: The required wrapper element (HTMLElement) was not provided.');
    }

    this._config = Object.assign({}, this._config, options);

    this._rangeInput = this._containerElement.querySelector(this._config.sliderSelector);
    if (!this._rangeInput) {
      throw new Error(`SliderController: Input element not found using selector "${this._config.sliderSelector}".`);
    }

    this._valueDisplay = this._containerElement.querySelector(this._config.sliderValueSelector);
    if (!this._valueDisplay) {
      console.warn(
        `SliderController: Value display element not found using selector "${this._config.sliderValueSelector}". The slider will still function.`
      );
    }

    this._initializeControls();
  }

  /**
   * @param {TSliderOptions} options An object containing new options to set.
   * @public
   * @description Sets multiple configuration options at once. If an option corresponds to a setter (e.g., `__value`), the setter is used.
   * @example
   * handler.__setOptions({ min: 1, max: 10, value: 5 });
   */
  __setOptions(options) {
    this._config = Object.assign(this._config, options);
    this.__min = this._config.min;
    this.__max = this._config.max;
    this.__step = this._config.step;
    this.__value = this._config.value;
  }

  /**
   * @param {TChangeCallback} callback The function to execute on slider value change ('input' event).
   * @public
   * @description Registers a callback function to be executed whenever the slider value changes. Can only register one listener.
   * @example
   * handler.__onValueChange((v, opts) => console.log(v, opts.min));
   */
  __onValueChange(callback) {
    if (!callback || this._changeListener) return;
    this._changeListener = callback;
    // Attach listener for 'input' event
    this._abortControllers[this._abortIndex++] = addMultiEvent(this._rangeInput, 'input', () => (this.__value = +this._rangeInput.value));
  }

  /**
   * @public
   * @description Toggles the visibility and enabled state of the slider (show/hide).
   */
  __toggleVisibility() {
    this._isActive ? this.__hide() : this.__show();
  }

  /**
   * @public
   * @description Toggles a CSS class (`position-left`) on the wrapper, typically for moving the control horizontally.
   */
  __togglePositionLeft() {
    this._containerElement.classList.toggle('position-left');
  }

  /**
   * @public
   * @description Enables the slider and adds the CSS class `show` to the wrapper.
   */
  __show() {
    this._rangeInput.disabled = false;
    this._isActive = true;
    this._containerElement.classList.add('show');
  }

  /**
   * @public
   * @description Disables the slider and removes the CSS class `show` from the wrapper.
   */
  __hide() {
    this._rangeInput.disabled = true;
    this._isActive = false;
    this._containerElement.classList.remove('show');
  }

  /**
   * @public
   * @description Cleans up all resources: aborts all event listeners and nullifies internal references.
   */
  __dispose() {
    this._abortControllers.forEach(controller => controller.abort());
    this._abortControllers.length = 0;
    // Nullify references to aid garbage collection
    this._containerElement = this._rangeInput = this._valueDisplay = this._changeListener = null;
  }

  /**
   * @private
   * @description Initial setup method: sets initial state, applies config to DOM attributes, and attaches drag listeners.
   */
  _initializeControls() {
    // Set initial active state based on wrapper's existing class
    if (!this._containerElement.classList.contains('show')) {
      this._rangeInput.disabled = true;
      this._isActive = false;
    } else {
      this._rangeInput.disabled = false;
      this._isActive = true;
    }

    // Apply configuration to the DOM via setters
    this.__min = this._config.min;
    this.__max = this._config.max;
    this.__step = this._config.step;
    this.__value = this._config.value;

    // Add drag listeners for CSS styling
    this._abortControllers[this._abortIndex++] = addMultiEvent(
      this._rangeInput,
      ['mousedown', 'touchstart'],
      () => this._rangeInput.classList.add('is-dragging'),
      { passive: true }
    );
    this._abortControllers[this._abortIndex++] = addMultiEvent(this._rangeInput, ['mouseup', 'touchend', 'change'], () =>
      this._rangeInput.classList.remove('is-dragging')
    );
  }

  /**
   * @param {number} value The numeric value to be displayed.
   * @private
   * @description Updates the inner text of the `_valueDisplay` element with the current slider value, appending 'x'.
   */
  _updateValueDisplay(value) {
    if (!this._valueDisplay) return;
    this._valueDisplay.innerText = value.toString() + 'x';
  }
}
